import React, { useEffect, useState } from "react";
import { useCreatorDashboardPageQueries } from "../../../../query/hooks";
import {
  Box,
  Typography,
  Grid,
  Button,
  Avatar,
  CircularProgress,
  ButtonGroup,
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import MultiRingChart from "./MultiRingChart";
import StackedRevenueChart from "./StackedRevenueChart";
import MyEarningsSection from "./MyEarningsSection";
import ContentAnalyticsSection from "./ContentAnalyticsSection";
import PayoutStatusOverview from "./PayoutStatusOverview";
import ContentUploadVolumeChart from "./ContentUploadVolumeChart";
import TransactionVolumeChart from "./TransactionVolumeChart";
import { useAchievementsStyles } from "./style";
import { getFileByIdAPI } from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import defaultAvatars from "../../../../assets/defaultAvatars.svg";
import { useHistory, useLocation } from "react-router-dom";
import arrowRightAna from "../../../../assets/images/arrowRightAna.svg";
import useSEO from "../../../../seo/use-seo";
import StandardPageHelmet from "../../../../seo/components/StandardPageHelmet";

export interface IDashboardSummary {
  currentBalance: number;
  totalEarningsThisMonth: number;
  totalEarningsChange: number;
  totalTipsThisMonth: number;
  totalTipsChange: number;
  newFollowersThisMonth: number;
  newFollowersChange: number;
}

export interface IDashboardEarningsOverview {
  total: number;
  tips: number;
  subscriptions: number;
  customVideoRequests: number;
  sales: number;
}

export interface IRevenueMonth {
  month: string;
  tips: number;
  subscriptions: number;
  customVideoRequests: number;
  sales: number;
  postPurchases: number;
}

export interface ITopEarner {
  userId: string;
  username: string;
  name: string;
  profileImg?: string;
  totalAmount: number;
}

const DashboardPage = () => {
  const seo = useSEO("dashboard");
  const classes = useAchievementsStyles();
  const {
    summary,
    loading,
    mainError,
    overview,
    overviewLoading,
    revenueData,
    revenueLoading,
    topTippers,
    topSubscribers,
    topCustomVideoBuyers,
    topPostBuyers,
  } = useCreatorDashboardPageQueries();
  const [user, setUser] = useState<{
    firstName: string;
    lastName: string;
  } | null>(null);
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [loadingMedia, setLoadingMedia] = useState<{ [key: string]: boolean }>(
    {}
  );
  const history = useHistory();
  const location = useLocation();

  const isUserAdmin = (u: any) =>
    !!(
      u?.isAdmin ||
      u?.role === "admin" ||
      u?.roles?.includes?.("admin") ||
      u?.userType?.title === "admin"
    );

  const [isAdmin, setIsAdmin] = useState(false);

  // Функція для витягування URL аватарки
  const fetchMediaUrl = async (mediaUrl: string) => {
    if (!mediaUrl || mediaUrl === "defaultAvatars") return defaultAvatars;
    if (mediaUrls[mediaUrl]) return mediaUrls[mediaUrl];

    // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
    if (mediaUrl.includes("lh3.googleusercontent.com")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      return mediaUrl;
    }

    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      return mediaUrl;
    }

    setLoadingMedia(prev => ({ ...prev, [mediaUrl]: true }));
    try {
      const fileId = mediaUrl.split("/api/files/")[1];
      if (!fileId) throw new Error("Invalid file ID");
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching avatar for ${mediaUrl}:`, err);
      showToast({ message: "Error fetching avatar", err: true });
      return defaultAvatars;
    } finally {
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
    }
  };

  useEffect(() => {
    if (mainError) {
      showToast({ message: "Error loading dashboard data", err: true });
    }
  }, [mainError]);

  // Завантаження даних користувача з localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAdmin(isUserAdmin(parsedUser));
      } catch (err) {
        console.error("Failed to parse user from localStorage", err);
      }
    }
  }, []);

  // Завантаження аватарок для всіх топ-користувачів
  useEffect(() => {
    const allUsers = [
      ...topTippers,
      ...topSubscribers,
      ...topCustomVideoBuyers,
      ...topPostBuyers,
    ];
    allUsers.forEach(user => {
      if (
        user.profileImg &&
        !mediaUrls[user.profileImg] &&
        !loadingMedia[user.profileImg]
      ) {
        fetchMediaUrl(user.profileImg);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topTippers, topSubscribers, topCustomVideoBuyers, topPostBuyers]);

  const total =
    (overview?.tips || 0) +
    (overview?.subscriptions || 0) +
    (overview?.customVideoRequests || 0) +
    (overview?.sales || 0);

  const getPercentage = (value: number) =>
    total > 0 ? `${Math.round((value / total) * 100)}%` : "0%";

  const totalRevenue = revenueData.reduce((acc, month) => {
    return (
      acc +
      month.tips +
      month.subscriptions +
      (month.customVideoRequests || 0) +
      (month.sales || 0) +
      (month.postPurchases || 0)
    );
  }, 0);

  const formatRevenue = (value: number) => {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Box className={classes.container}>
        {/* Admin-only switcher */}
        {isAdmin && (
          <Box className={classes.analyticsSwitcher}>
            <ButtonGroup variant="outlined" size="small">
              <Button
                className={classes.analyticsTab}
                variant={
                  !location.pathname.startsWith("/adminDashboard")
                    ? "contained"
                    : "outlined"
                }
                onClick={() => history.push("/dashboard")}
              >
                Creator analytics
              </Button>
              <Button
                className={classes.analyticsTab}
                variant={
                  location.pathname.startsWith("/adminDashboard")
                    ? "contained"
                    : "outlined"
                }
                onClick={() => history.push("/adminDashboard")}
              >
                Admin analytics
              </Button>
            </ButtonGroup>
          </Box>
        )}

        <div className={classes.containerr}>
          <Typography className={classes.welcome}>
            Welcome back, {user?.firstName} {user?.lastName}
          </Typography>
          <Typography className={classes.subtitle}>
            Track, manage and forecast your Account
          </Typography>

          <Box className={classes.balanceContainer}>
            <div className={classes.balanceContainerDiv}>
              <Typography className={classes.balanceTitle}>
                Current Balance
              </Typography>
              <Typography className={classes.balanceAmount}>
                {loading
                  ? "..."
                  : `$${(summary?.currentBalance ?? 0).toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}`}
              </Typography>
            </div>
            <Box className={classes.balanceActions}>
              <Button
                variant="outlined"
                className={classes.transactionsHistoryButton}
                onClick={() => history.push("/creator-payouts")}
              >
                Transactions History
              </Button>
              <Button
                variant="contained"
                className={classes.withdrawButton}
                onClick={() => history.push("/creator-payouts")}
                startIcon={
                  <img src={arrowRightAna} alt="" width={18} height={18} />
                }
              >
                Withdraw
              </Button>
            </Box>
          </Box>

          <MyEarningsSection
            summary={summary}
            overview={overview}
            loading={loading}
            overviewLoading={overviewLoading}
          />

          <ContentAnalyticsSection />

          {/* Content Upload Volume Chart */}
          <Box style={{ marginBottom: "54px" }}>
            <ContentUploadVolumeChart />
          </Box>

          {/* Transaction Volume Chart */}
          <Box style={{ marginBottom: "54px" }}>
            <TransactionVolumeChart />
          </Box>

          <Grid container spacing={3} className={classes.centre}>
            <Grid item xs={12} className={classes.centreLeft}>
              <Box className={classes.centreCard}>
                <div className={classes.cardHelpTop}>
                  <Typography className={classes.cardTitle}>
                    Earnings Overview
                  </Typography>
                </div>
                <div className={classes.centreCardHelp}>
                  <div className={classes.doughnutContainer}>
                    <MultiRingChart
                      data={{
                        tips: overview?.tips || 0,
                        subscriptions: overview?.subscriptions || 0,
                        customVideoRequests: overview?.customVideoRequests || 0,
                        sales: overview?.sales || 0,
                        total: overview?.total || 0,
                        loading: overviewLoading,
                      }}
                    />
                  </div>
                  <Box className={classes.legend}>
                    <Box className={classes.legendItem}>
                      <span
                        className={classes.legendColor}
                        style={{ backgroundColor: "#FE5A97" }}
                      ></span>
                      <Typography>Tips</Typography>
                      <Typography className={classes.legendValue}>
                        {getPercentage(overview?.tips || 0)}
                      </Typography>
                    </Box>
                    <Box className={classes.legendItem}>
                      <span
                        className={classes.legendColor}
                        style={{ backgroundColor: "#FFD700" }}
                      ></span>
                      <Typography>Subscriptions</Typography>
                      <Typography className={classes.legendValue}>
                        {getPercentage(overview?.subscriptions || 0)}
                      </Typography>
                    </Box>
                    <Box className={classes.legendItem}>
                      <span
                        className={classes.legendColor}
                        style={{ backgroundColor: "#808080" }}
                      ></span>
                      <Typography>Custom Video</Typography>
                      <Typography className={classes.legendValue}>
                        {getPercentage(overview?.customVideoRequests || 0)}
                      </Typography>
                    </Box>
                    <Box className={classes.legendItem}>
                      <span
                        className={classes.legendColor}
                        style={{ backgroundColor: "#4682B4" }}
                      ></span>
                      <Typography>Posts</Typography>
                      <Typography className={classes.legendValue}>
                        {getPercentage(overview?.sales || 0)}
                      </Typography>
                    </Box>
                  </Box>
                </div>
              </Box>
            </Grid>
            <Grid item xs={12} className={classes.centreRight}>
              <Box className={classes.centreCardBot}>
                <div className={classes.cardHelpTop}>
                  <Typography className={classes.cardTitle}>Revenue</Typography>
                  <Typography className={classes.dateRange}>
                    Jan 2024 - Dec 2024
                  </Typography>
                </div>
                <div className={classes.cardHelp}>
                  <Typography className={classes.cardValue}>
                    {revenueLoading ? "..." : formatRevenue(totalRevenue)}
                  </Typography>
                  <Typography className={classes.cardChange}>
                    <TrendingUpIcon fontSize="small" /> 14.5% vs last year
                  </Typography>
                </div>
                <div>
                  <StackedRevenueChart
                    data={revenueData}
                    loading={revenueLoading}
                  />
                </div>
              </Box>
            </Grid>
          </Grid>

          {/* Payout Status Overview */}
          <Box style={{ marginBottom: "24px" }}>
            <PayoutStatusOverview
              onRequestPayout={() => {
                // Navigate to payouts page or open payout request dialog
                history.push("/settings/payouts");
              }}
              onViewAll={() => {
                // Navigate to full payouts page
                history.push("/settings/payouts");
              }}
            />
          </Box>

          <Grid className={classes.fotter}>
            <Grid className={classes.fotterLeft}>
              <Box className={classes.subscribersBox}>
                <Typography className={classes.sectionTitle}>
                  Top Tippers
                </Typography>
                <Box className={classes.subscribersBoxx}>
                  {topTippers.map((user, i) => (
                    <Box key={i} className={classes.subscriberItem}>
                      <div className={classes.subscriberItemm}>
                        <Avatar
                          src={
                            mediaUrls[user.profileImg || ""] || defaultAvatars
                          }
                          className={classes.avatar}
                        >
                          {loadingMedia[user.profileImg || ""] && (
                            <CircularProgress size={24} />
                          )}
                        </Avatar>
                        <Box>
                          <Typography className={classes.name}>
                            {user.name}
                          </Typography>
                          <Typography className={classes.username}>
                            @{user.username}
                          </Typography>
                        </Box>
                      </div>
                      <Typography className={classes.likes}>
                        ${user.totalAmount}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <div className={classes.subscriberItemBot}>View All</div>
              </Box>
            </Grid>
            <Grid className={classes.fotterLeft}>
              <Box className={classes.subscribersBox}>
                <Typography className={classes.sectionTitle}>
                  Top Subscribers
                </Typography>
                <Box className={classes.subscribersBoxx}>
                  {topSubscribers.map((user, i) => (
                    <Box key={i} className={classes.subscriberItem}>
                      <div className={classes.subscriberItemm}>
                        <Avatar
                          src={
                            mediaUrls[user.profileImg || ""] || defaultAvatars
                          }
                          className={classes.avatar}
                        >
                          {loadingMedia[user.profileImg || ""] && (
                            <CircularProgress size={24} />
                          )}
                        </Avatar>
                        <Box>
                          <Typography className={classes.name}>
                            {user.name}
                          </Typography>
                          <Typography className={classes.username}>
                            @{user.username}
                          </Typography>
                        </Box>
                      </div>
                      <Typography className={classes.likes}>
                        ${user.totalAmount}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <div className={classes.subscriberItemBot}>View All</div>
              </Box>
            </Grid>
            <Grid className={classes.fotterLeft}>
              <Box className={classes.subscribersBox}>
                <Typography className={classes.sectionTitle}>
                  Top Custom Video Buyers
                </Typography>
                <Box className={classes.subscribersBoxx}>
                  {topCustomVideoBuyers.map((user, i) => (
                    <Box key={i} className={classes.subscriberItem}>
                      <div className={classes.subscriberItemm}>
                        <Avatar
                          src={
                            mediaUrls[user.profileImg || ""] || defaultAvatars
                          }
                          className={classes.avatar}
                        >
                          {loadingMedia[user.profileImg || ""] && (
                            <CircularProgress size={24} />
                          )}
                        </Avatar>
                        <Box>
                          <Typography className={classes.name}>
                            {user.name}
                          </Typography>
                          <Typography className={classes.username}>
                            @{user.username}
                          </Typography>
                        </Box>
                      </div>
                      <Typography className={classes.likes}>
                        ${user.totalAmount}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <div className={classes.subscriberItemBot}>View All</div>
              </Box>
            </Grid>
            <Grid className={classes.fotterRight}>
              <Box className={classes.earningsBox}>
                <Typography className={classes.sectionTitle}>
                  Top Post Buyers
                </Typography>
                <Box className={classes.subscribersBoxx}>
                  {topPostBuyers.map((user, i) => (
                    <Box key={i} className={classes.earningItem}>
                      <div className={classes.subscriberItemm}>
                        <Avatar
                          src={
                            mediaUrls[user.profileImg || ""] || defaultAvatars
                          }
                          className={classes.avatar}
                        >
                          {loadingMedia[user.profileImg || ""] && (
                            <CircularProgress size={24} />
                          )}
                        </Avatar>
                        <Box>
                          <Typography className={classes.name}>
                            {user.name}
                          </Typography>
                          <Typography className={classes.username}>
                            @{user.username}
                          </Typography>
                        </Box>
                      </div>
                      <Typography className={classes.earnings}>
                        ${user.totalAmount}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <div className={classes.subscriberItemBot}>View All</div>
              </Box>
            </Grid>
          </Grid>
        </div>
      </Box>
    </>
  );
};

export default DashboardPage;
